import { reactive, watch } from "vue";
import {
  getDatabase,
  ref as dbRef,
  onValue,
  query,
  orderByKey,
  startAt,
} from "firebase/database";
import { authStore } from "@/store/auth";
import {
  computeRanges,
  dateKeyFor,
  fetchStartKey,
  resolveTimeZone,
  UTC,
} from "@/scripts/spokenRanges.js";

// Voice Ledger client store: the signed-in user's real speaking time, ready
// for the LessonsView card and the Progress page. It subscribes to the
// account's accounting timezone and selected language, then to that
// language's daily buckets and lifetime counter, and turns them into the five
// ranges via the pure helpers in @/scripts/spokenRanges.
//
// Only the buckets the ranges need are fetched (orderByKey + startAt from
// fetchStartKey, at most ~366 tiny nodes), and lifetimeSpokenSeconds is read
// as its own leaf: stats/{uid}/{lang} now carries the whole daily map, so
// reading that node wholesale to get one number would grow into a real
// payload over a year of practice.
//
// CONTRACT for readers:
//   - `ranges` is always safe to render.
//   - The calendar ranges (today/week/month/year, practiceDaysThisWeek) are
//     trustworthy only when `loaded` is true. A zero today is a REAL zero only
//     then, so the card's zero-state invitation must check it: when the
//     accounting timezone is unknown the calendar ranges are deliberately
//     zeroed, and showing the invitation off that would tell a learner who did
//     practice that they had not.
//   - `allTimeSeconds` is trustworthy only when `allTimeLoaded` is true. It is
//     tracked separately because the reads fail separately: the lifetime read
//     can fail while the buckets succeed, and a UI that fell back to
//     "all time only" on a generic error flag would confidently render zero to
//     a learner with hours of practice.
//   - `error` means at least one read failed, for messaging only. Never decide
//     what to render from it alone; use the two flags above.
//
// Reads fail soft; nothing here may throw into LessonsView.
//
// Spec: docs/superpowers/specs/2026-07-23-voice-ledger-design.md

// RTDB path segments may not contain '.', '#', '$', '[', ']' or control
// characters, and ref() THROWS on one that does. selectedLanguage is a
// client-writable string, so it is treated as untrusted input rather than
// interpolated straight into a path. Deliberately a character whitelist
// rather than a lookup in @/data/Language.js: a language added on the server
// before this bundle ships must still resolve, and the whitelist is what
// keeps the path legal.
const LANGUAGE_CODE_PATTERN = /^[A-Za-z0-9-]{1,32}$/;

// How often an idle tab re-checks whether the local date rolled over.
// visibilitychange covers backgrounded tabs; this covers one left in the
// foreground, where no event fires and no snapshot may arrive for hours.
const DAY_ROLLOVER_CHECK_MS = 60_000;

// The backend stamps every message createdAt from the SERVER clock and keys
// the bucket off that, so the client has to ask what time the server thinks it
// is. A device whose clock is off by hours (a manual change, a flat battery)
// would otherwise look up a bucket key the server never wrote and show a false
// zero for today. RTDB exposes the drift on this special path; it resolves
// immediately from cache and needs no rules.
const SERVER_TIME_OFFSET_PATH = ".info/serverTimeOffset";

const emptyRanges = () => ({
  todaySeconds: 0,
  weekSeconds: 0,
  monthSeconds: 0,
  yearSeconds: 0,
  allTimeSeconds: 0,
  practiceDaysThisWeek: 0,
});

export const spokenStats = reactive({
  loaded: false,
  allTimeLoaded: false,
  error: false,
  language: null,
  timeZone: UTC,
  ranges: emptyRanges(),
});

let buckets = {};
let lifetimeSeconds = 0;
let userUnsubs = [];
let dailyUnsubs = [];
let lifetimeUnsubs = [];
let infoUnsubs = [];
let serverTimeOffsetMs = 0;
let stopUserWatch = null;
let stopDocumentWatch = null;
let rolloverTimer = null;
let currentUserId = null;
let accountLanguage = null;
let languageOverride = null;
// A read that resolved: either a stored zone or a confirmed absent one. False
// while the profile read is still in flight AND after it fails, which are the
// two states where no calendar boundary can be trusted.
let timeZoneKnown = false;
let lastDayKey = null;
// The startAt bound the live bucket query was built with. "Now" can move
// under us (a server-offset correction, or a tab open across New Year), and a
// bound computed from the old instant can exclude buckets the ranges need.
let subscribedFetchStart = null;
// `error` is derived from these rather than assigned directly: three
// independent reads can fail, and a single last-writer-wins flag let a
// recovering one silently clear another's failure.
let timeZoneReadFailed = false;
let languageReadFailed = false;
let languageUnusable = false;
let dailyReadFailed = false;
let lifetimeReadFailed = false;

function refreshError() {
  spokenStats.error =
    timeZoneReadFailed ||
    languageReadFailed ||
    languageUnusable ||
    dailyReadFailed ||
    lifetimeReadFailed;
}
// Callbacks capture the generation they were registered under and drop
// anything delivered after a teardown, so an in-flight snapshot from the
// previous account or language can never repopulate the new one.
let userGeneration = 0;
let dailyGeneration = 0;
let lifetimeGeneration = 0;

function serverNowMs() {
  return Date.now() + serverTimeOffsetMs;
}

function recompute() {
  if (!timeZoneKnown) {
    // Without the accounting timezone there is no honest calendar boundary:
    // the server keyed every bucket in a zone we cannot name, so today/week/
    // month/year would be guesses. All-time needs no boundary and survives.
    const lifetime = Number(lifetimeSeconds);
    lastDayKey = null;
    spokenStats.ranges = {
      ...emptyRanges(),
      allTimeSeconds: Number.isFinite(lifetime) && lifetime > 0 ? lifetime : 0,
    };
    return;
  }
  const nowMs = serverNowMs();
  lastDayKey = dateKeyFor(nowMs, spokenStats.timeZone);
  spokenStats.ranges = computeRanges({
    buckets,
    lifetimeSeconds,
    nowMs,
    timeZone: spokenStats.timeZone,
  });
}

// Every range boundary is derived from "now", so the clock is an input as
// much as the buckets are: a tab open across local midnight must stop
// reporting yesterday as today even if no new data ever arrives.
function recomputeIfDayChanged() {
  if (!timeZoneKnown) return;
  if (dateKeyFor(serverNowMs(), spokenStats.timeZone) === lastDayKey) return;
  refreshForCurrentInstant();
}

// Recompute, or re-read first when "now" moved far enough to change which
// buckets the query must cover. Routed through one place rather than reasoning
// about which direction each caller can move the clock.
function refreshForCurrentInstant() {
  if (
    subscribedFetchStart !== null &&
    timeZoneKnown &&
    fetchStartKey(serverNowMs(), spokenStats.timeZone) !== subscribedFetchStart
  ) {
    // Only the bucket window is clock-dependent. The lifetime counter is not,
    // so it keeps its listener and its already-confirmed value: dropping it
    // here would blank a learner's all-time total over a calendar detail, and
    // leave it blank for good if the replacement read failed.
    subscribeDaily(currentUserId);
    return;
  }
  recompute();
}

function tearDown(unsubs) {
  while (unsubs.length) unsubs.pop()();
}

function tearDownStats() {
  tearDown(dailyUnsubs);
  tearDown(lifetimeUnsubs);
  dailyGeneration += 1;
  lifetimeGeneration += 1;
  subscribedFetchStart = null;
}

// The language node this user's stats live under, or null when the store is
// not in a state that can read anything.
function statsPathFor(userId) {
  const language = spokenStats.language;
  if (!userId || !language || languageUnusable) return null;
  return `stats/${userId}/${language}`;
}

// Constructing a listener must not take LessonsView down with it.
function failSoft(error, unsubs) {
  console.error("spokenStats: could not subscribe to stats", error);
  tearDown(unsubs);
}

function subscribeDaily(userId) {
  tearDown(dailyUnsubs);
  dailyGeneration += 1;
  const generation = dailyGeneration;
  buckets = {};
  subscribedFetchStart = null;
  spokenStats.loaded = false;
  // Each read owns its own failure flag: a shared one let a recovered bucket
  // read leave `error` stuck true (or clear a live lifetime failure).
  dailyReadFailed = false;
  refreshError();
  recompute();

  const statsPath = statsPathFor(userId);
  // The timezone gates the BUCKET read rather than defaulting to UTC: reading
  // first would compute a full set of ranges against the wrong calendar and
  // flash them before the real zone lands.
  if (!statsPath || !timeZoneKnown) return;

  const fetchStart = fetchStartKey(serverNowMs(), spokenStats.timeZone);
  try {
    const unsub = onValue(
      query(dbRef(getDatabase(), `${statsPath}/daily`), orderByKey(), startAt(fetchStart)),
      (snap) => {
        if (generation !== dailyGeneration) return;
        buckets = snap.val() || {};
        spokenStats.loaded = true;
        // A snapshot arriving is proof this read works, whoever re-established
        // it, so it clears its own failure and nobody else's.
        dailyReadFailed = false;
        refreshError();
        recompute();
      },
      () => {
        if (generation !== dailyGeneration) return;
        spokenStats.loaded = false;
        dailyReadFailed = true;
        refreshError();
        recompute();
      }
    );
    subscribedFetchStart = fetchStart;
    dailyUnsubs.push(unsub);
  } catch (error) {
    failSoft(error, dailyUnsubs);
    dailyReadFailed = true;
    refreshError();
  }
}

function subscribeLifetime(userId) {
  tearDown(lifetimeUnsubs);
  lifetimeGeneration += 1;
  const generation = lifetimeGeneration;
  lifetimeSeconds = 0;
  spokenStats.allTimeLoaded = false;
  lifetimeReadFailed = false;
  refreshError();

  const statsPath = statsPathFor(userId);
  // No calendar involved, so this proceeds even while the zone is unknown and
  // all-time still renders.
  if (!statsPath) return;

  try {
    lifetimeUnsubs.push(
      onValue(
        dbRef(getDatabase(), `${statsPath}/lifetimeSpokenSeconds`),
        (snap) => {
          if (generation !== lifetimeGeneration) return;
          lifetimeSeconds = snap.val() || 0;
          spokenStats.allTimeLoaded = true;
          lifetimeReadFailed = false;
          refreshError();
          recompute();
        },
        () => {
          if (generation !== lifetimeGeneration) return;
          spokenStats.allTimeLoaded = false;
          lifetimeReadFailed = true;
          refreshError();
        }
      )
    );
  } catch (error) {
    failSoft(error, lifetimeUnsubs);
    lifetimeReadFailed = true;
    refreshError();
  }
}

// Both reads, for when the account or the language itself changed. The
// timezone path deliberately does NOT come through here: only the bucket
// window depends on the zone.
function subscribeStats(userId) {
  subscribeDaily(userId);
  subscribeLifetime(userId);
  recompute();
}

function applyLanguage(language, userId) {
  // Validated once, here, rather than at each read: selectedLanguage is a
  // client-writable string and a value with an RTDB-illegal character would
  // make ref() throw.
  languageUnusable = Boolean(language) && !LANGUAGE_CODE_PATTERN.test(language);
  if (languageUnusable) {
    console.error("spokenStats: refusing unusable language code", language);
  }
  spokenStats.language = language || null;
  subscribeStats(userId);
}

export function initSpokenStats() {
  if (stopUserWatch) return; // LessonsView and ProgressView both call this

  let db;
  try {
    db = getDatabase();
  } catch (error) {
    console.error("spokenStats: database unavailable", error);
    spokenStats.error = true;
    return;
  }
  stopUserWatch = watch(
    () => authStore.userId,
    (userId) => {
      tearDown(userUnsubs);
      tearDownStats();
      userGeneration += 1;
      const generation = userGeneration;
      currentUserId = userId || null;
      accountLanguage = null;
      // An override belongs to the account that set it; carrying it across a
      // sign-in would show the new user a language they may not even hold.
      languageOverride = null;
      buckets = {};
      lifetimeSeconds = 0;
      timeZoneKnown = false;
      timeZoneReadFailed = false;
      languageReadFailed = false;
      languageUnusable = false;
      dailyReadFailed = false;
      lifetimeReadFailed = false;
      spokenStats.language = null;
      spokenStats.timeZone = UTC;
      spokenStats.loaded = false;
      spokenStats.allTimeLoaded = false;
      spokenStats.error = false;
      spokenStats.ranges = emptyRanges();
      if (!userId) return;

      try {
        subscribeUser(db, userId, generation);
      } catch (error) {
        // watch() runs this synchronously during a component's setup, so an
        // SDK throw here would take LessonsView down with it.
        console.error("spokenStats: could not subscribe to the profile", error);
        tearDown(userUnsubs);
        languageReadFailed = true;
        timeZoneReadFailed = true;
        refreshError();
      }
    },
    { immediate: true }
  );

  startClock(db);
}

function subscribeUser(db, userId, generation) {
  userUnsubs.push(
    onValue(
      dbRef(db, `users/${userId}/timezone`),
      (snap) => {
        if (generation !== userGeneration) return;
        // The profile zone, never the device zone: the server keys every
        // bucket by it, so reading with the device zone would miss today's
        // bucket outright for a traveling user. A missing or unusable stored
        // value resolves to UTC exactly as the backend helpers do, which is a
        // known zone, not an unknown one.
        spokenStats.timeZone = resolveTimeZone(snap.val());
        timeZoneKnown = true;
        timeZoneReadFailed = false;
        refreshError();
        subscribeDaily(userId);
      },
      () => {
        if (generation !== userGeneration) return;
        // An unreadable zone is NOT the same as an absent one: the server may
        // well have bucketed in America/New_York, so falling back to UTC here
        // would render confidently wrong day boundaries.
        timeZoneKnown = false;
        spokenStats.timeZone = UTC;
        timeZoneReadFailed = true;
        refreshError();
        subscribeDaily(userId);
      }
    )
  );
  userUnsubs.push(
    onValue(
      dbRef(db, `users/${userId}/selectedLanguage`),
      (snap) => {
        if (generation !== userGeneration) return;
        languageReadFailed = false;
        refreshError();
        accountLanguage = snap.val() || null;
        if (languageOverride) return; // the Progress switcher wins
        applyLanguage(accountLanguage, userId);
      },
      () => {
        if (generation !== userGeneration) return;
        languageReadFailed = true;
        refreshError();
      }
    )
  );
}

// The day-rollover machinery, plus the server clock the day key depends on.
function startClock(db) {
  infoUnsubs.push(
    onValue(
      dbRef(db, SERVER_TIME_OFFSET_PATH),
      (snap) => {
        serverTimeOffsetMs = Number(snap.val()) || 0;
        // A correction can move "now" across a year boundary, which changes
        // which buckets the query has to cover, so this may re-read rather
        // than just recompute.
        refreshForCurrentInstant();
      },
      () => {
        // No offset available: fall back to the device clock, which is the
        // best guess left and still correct for the overwhelming majority.
        serverTimeOffsetMs = 0;
      }
    )
  );

  if (typeof document !== "undefined") {
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") return;
      recomputeIfDayChanged();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    stopDocumentWatch = () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
    // Inside the DOM guard on purpose: in a Node/SSR context there is no UI to
    // keep current and an orphaned interval would hold the event loop open.
    rolloverTimer = setInterval(recomputeIfDayChanged, DAY_ROLLOVER_CHECK_MS);
  }
}

/**
 * Point the store at one language regardless of the account's selected one,
 * for the Progress page's switcher. Pass a falsy value to hand control back
 * to `users/{uid}/selectedLanguage`.
 * @param {string|null} code
 */
export function setSpokenStatsLanguage(code) {
  languageOverride = code || null;
  applyLanguage(languageOverride || accountLanguage, currentUserId);
}

// Test-only: tear down every subscription and reset state so each test can
// call initSpokenStats() against a fresh mock.
export function _resetSpokenStatsForTests() {
  tearDown(userUnsubs);
  tearDownStats();
  tearDown(infoUnsubs);
  serverTimeOffsetMs = 0;
  userGeneration += 1;
  if (stopUserWatch) stopUserWatch();
  stopUserWatch = null;
  if (stopDocumentWatch) stopDocumentWatch();
  stopDocumentWatch = null;
  if (rolloverTimer) clearInterval(rolloverTimer);
  rolloverTimer = null;
  buckets = {};
  lifetimeSeconds = 0;
  currentUserId = null;
  accountLanguage = null;
  languageOverride = null;
  timeZoneKnown = false;
  timeZoneReadFailed = false;
  languageReadFailed = false;
  languageUnusable = false;
  dailyReadFailed = false;
  lifetimeReadFailed = false;
  lastDayKey = null;
  subscribedFetchStart = null;
  spokenStats.loaded = false;
  spokenStats.allTimeLoaded = false;
  spokenStats.error = false;
  spokenStats.language = null;
  spokenStats.timeZone = UTC;
  spokenStats.ranges = emptyRanges();
}
